import { forwardRef, useId, useState } from "react";
import { HexColorPicker, HexColorInput } from "react-colorful";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * ColorPicker — react-colorful HexColorPicker'ga Aurora dark theme moslashuv.
 *
 * Faza 2 use-case: TenantBrandingForm (primaryColor, accentColor, gradientFrom/To).
 *
 * Features:
 *   - Aurora-themed picker (saturation + hue + alpha bars)
 *   - Hex input (eski Aurora preset bilan placeholder)
 *   - Reset to default tugma (aurora preset rangi)
 *   - Contrast warning: agar tanlangan rang dark bg'da o'qib bo'lmasa (relativeLuminance < 0.3)
 *
 * A11y:
 *   - aria-label color picker
 *   - HexColorInput Tab orqali kelinadi
 *   - Saturation va hue bar — mouse va touch (react-colorful keyboard a11y minimal,
 *     hozircha hex input asosiy a11y path)
 */
export interface ColorPickerProps {
  value: string; // #RRGGBB
  onChange: (next: string) => void;
  label?: string;
  /** Reset bosilganda qaytadigan rang (default Aurora violet) */
  defaultColor?: string;
  /** Kontrast warning'ni o'chirib qo'yish */
  hideContrastHint?: boolean;
  className?: string;
  id?: string;
}

const HEX_REGEX = /^#[0-9a-fA-F]{6}$/;

/**
 * Brand primary hex (Claude Peach) — react-colorful hex string talab qiladi,
 * tokens.css'dagi `--brand-primary` ning literal qiymati. Tokenni yangilashda
 * shu joyni ham yangilang.
 */
const DEFAULT_PRIMARY_HEX = "#8B5CF6";

/**
 * Relative luminance (WCAG) — dark fon ustida o'qiladimi.
 * 0..1, dark fon (text-on-dark) uchun >= 0.30 yetarli (taxminan).
 */
function relativeLuminance(hex: string): number {
  if (!HEX_REGEX.test(hex)) return 0.5;
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const adj = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * adj(r) + 0.7152 * adj(g) + 0.0722 * adj(b);
}

export const ColorPicker = forwardRef<HTMLDivElement, ColorPickerProps>(
  (
    {
      value,
      onChange,
      label,
      // Aurora/Peach brand-primary hex (foydalanuvchi bu rangni tarmoqlaridan
      // ko'rinish uchun hex sifatida kutadi — react-colorful hex string talab qiladi).
      defaultColor = DEFAULT_PRIMARY_HEX,
      hideContrastHint = false,
      className,
      id,
    },
    ref,
  ) => {
    const reactId = useId();
    const inputId = id ?? `color-${reactId}`;
    const [internal, setInternal] = useState(value);

    const apply = (hex: string) => {
      setInternal(hex);
      if (HEX_REGEX.test(hex)) onChange(hex);
    };

    const lum = relativeLuminance(value);
    const lowContrast = lum < 0.18; // dark bg'da juda qoraga moyil

    return (
      <div
        ref={ref}
        className={cn("flex flex-col gap-3", className)}
        role="group"
        aria-label={label ?? "Rang tanlash"}
      >
        {label && (
          <div className="flex items-center justify-between">
            <label
              htmlFor={inputId}
              className="text-xs font-medium uppercase tracking-wider text-[var(--text-secondary)]"
            >
              {label}
            </label>
            <button
              type="button"
              onClick={() => apply(defaultColor)}
              className={cn(
                "inline-flex items-center gap-1 text-[11px] text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
                "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)]/60 rounded",
              )}
              aria-label="Standartga qaytarish"
            >
              <RotateCcw className="h-3 w-3" aria-hidden />
              Standart
            </button>
          </div>
        )}

        <HexColorPicker
          color={value}
          onChange={apply}
          className="aurora-color-picker"
        />

        <div className="flex items-center gap-2">
          <div
            aria-hidden
            className="h-9 w-9 shrink-0 rounded-md border border-[var(--border-strong)]"
            style={{ background: value }}
          />
          <div className="relative flex-1">
            <span
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs font-mono text-[var(--text-muted)]"
            >
              #
            </span>
            <HexColorInput
              id={inputId}
              color={internal}
              onChange={apply}
              prefixed={false}
              className={cn(
                "h-9 w-full rounded-md border bg-[var(--bg-base)]/60 pl-7 pr-3 font-mono text-sm uppercase",
                "border-[var(--border-strong)] text-[var(--text-primary)]",
                "focus:border-[var(--brand-primary)]/60 focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]/30",
              )}
              aria-label="Hex rang qiymati"
            />
          </div>
        </div>

        {!hideContrastHint && lowContrast && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border border-[var(--warning-border)] bg-[var(--warning-soft)] px-2.5 py-2"
          >
            <AlertTriangle
              className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[var(--warning-fg)]"
              aria-hidden
            />
            <p className="text-[11px] leading-tight text-[var(--warning-fg)]">
              Rang juda qora — dark fonda o'qib bo'lmasligi mumkin. Yorqinroq variant tavsiya etiladi
              (WCAG AA).
            </p>
          </div>
        )}
      </div>
    );
  },
);
ColorPicker.displayName = "ColorPicker";
